
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const FAQ: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Find answers to commonly asked questions about our platform, services, and more.
        </p>
      </div>
      
      <div className="max-w-4xl mx-auto">
        <Tabs defaultValue="platform" className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 mb-8">
            <TabsTrigger value="platform">Platform</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="accounts">Accounts</TabsTrigger>
            <TabsTrigger value="agents">Agents</TabsTrigger>
            <TabsTrigger value="technical">Technical</TabsTrigger>
          </TabsList>
          
          <TabsContent value="platform" className="mt-4">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>What is OGMAI?</AccordionTrigger>
                <AccordionContent>
                  OGMAI is a platform that allows users to discover, share, and use AI agents created by developers and enthusiasts around the world. Our platform connects AI creators with users looking to leverage artificial intelligence to enhance productivity and automate tasks.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger>How does OGMAI work?</AccordionTrigger>
                <AccordionContent>
                  OGMAI works by providing a marketplace for AI agents. Creators can build and publish their AI agents on our platform, while users can browse, purchase, and use these agents for various tasks. The platform handles the discovery, deployment, and payment processes, making it easy for both creators and users.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger>What kinds of AI agents are available?</AccordionTrigger>
                <AccordionContent>
                  Our platform hosts a wide variety of AI agents across multiple categories including Productivity, Creative, Business, Education, Development, Research, Data Analysis, Marketing, and more. These agents can help with tasks ranging from content creation to data analysis to automation of workflows.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger>Can I try agents before purchasing?</AccordionTrigger>
                <AccordionContent>
                  Yes, many agents offer free trials or demo versions. Additionally, we have free agents that you can use without any cost. Check the specific agent page for trial availability and features.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>
          
          <TabsContent value="pricing" className="mt-4">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>What pricing models are available?</AccordionTrigger>
                <AccordionContent>
                  OGMAI supports various pricing models including free, one-time purchases, subscriptions, usage-based pricing, and freemium models. The pricing for each agent is set by the creator and is displayed clearly on the agent's page.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger>Is there a subscription for the platform?</AccordionTrigger>
                <AccordionContent>
                  The OGMAI platform is free to join and browse. You only pay for the agents you choose to use, based on their individual pricing models. We may offer premium platform subscriptions in the future with additional benefits.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger>How do payments and refunds work?</AccordionTrigger>
                <AccordionContent>
                  Payments are processed securely through our platform. We accept major credit cards and digital payment methods. Refund policies are set by individual creators, but OGMAI reserves the right to intervene in case of disputes. Please check the refund policy on the agent's page before purchasing.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger>How do I fund my wallet?</AccordionTrigger>
                <AccordionContent>
                  You can add funds to your OGMAI wallet through your dashboard. Simply navigate to the wallet section, choose your preferred payment method, and enter the amount you wish to add. These funds can then be used to purchase or subscribe to agents.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>
          
          <TabsContent value="accounts" className="mt-4">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>What types of accounts are available?</AccordionTrigger>
                <AccordionContent>
                  OGMAI offers two primary account types: User accounts for those who want to discover and use AI agents, and Creator accounts for those who want to build and publish their own agents. Both accounts have access to the marketplace, but creators have additional tools for agent development and analytics.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger>How do I become a creator?</AccordionTrigger>
                <AccordionContent>
                  You can upgrade your regular user account to a creator account through your dashboard. Click on "Become a Creator" and follow the verification process. Once approved, you'll gain access to the creator tools and be able to publish your agents to the marketplace.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger>Can I have both user and creator features?</AccordionTrigger>
                <AccordionContent>
                  Yes, creator accounts have all the features of user accounts plus the ability to publish and manage AI agents. There's no need to maintain separate accounts.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger>What happens to my data when I delete my account?</AccordionTrigger>
                <AccordionContent>
                  When you delete your account, all your personal information is permanently removed from our systems within 30 days. However, if you've purchased agents, your license rights will be maintained according to the terms of purchase. For creators, published agents will be removed from the marketplace unless alternate arrangements are made.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>
          
          <TabsContent value="agents" className="mt-4">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>What types of agent files are supported?</AccordionTrigger>
                <AccordionContent>
                  OGMAI supports various agent file formats including Python scripts, JavaScript modules, JSON configuration files, and pre-trained models in common formats like ONNX, TensorFlow SavedModel, and PyTorch. Creators should check our technical documentation for detailed specifications.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger>How long do agents remain available?</AccordionTrigger>
                <AccordionContent>
                  Once you purchase an agent, it remains available in your library indefinitely, subject to the terms set by the creator. However, support and updates may have limited timeframes based on the creator's policies. For subscription-based agents, access is maintained only while your subscription is active.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger>Can I get updates for agents I've purchased?</AccordionTrigger>
                <AccordionContent>
                  Yes, many agents offer updates as part of their purchase or subscription plan. The update policy is determined by the creator and will be stated on the agent's page. Some creators offer lifetime updates, while others may have a limited update period.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger>How are agent categories organized?</AccordionTrigger>
                <AccordionContent>
                  Agents are organized into main categories like Productivity, Development, Creative, Business, etc. Each main category may have subcategories for more specific classification. Creators assign categories to their agents during publication, and our curation team ensures proper categorization for easy discovery.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>
          
          <TabsContent value="technical" className="mt-4">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>Which Large Language Models (LLMs) are supported?</AccordionTrigger>
                <AccordionContent>
                  OGMAI supports integration with major LLMs including OpenAI's GPT models, Anthropic's Claude, Google's Gemini, Meta's Llama, and other open-source models. Creators can specify which models their agents are compatible with, and users can connect their own API keys for these services.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2">
                <AccordionTrigger>How are agents deployed?</AccordionTrigger>
                <AccordionContent>
                  Agents can be deployed in various ways depending on their design. Some run entirely within your browser, others may require API access to our secure cloud environment, and some may require installation in your local environment. The deployment method is clearly indicated on each agent's page along with system requirements.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3">
                <AccordionTrigger>Is my data secure when using agents?</AccordionTrigger>
                <AccordionContent>
                  We take data security seriously. All data transmissions are encrypted, and we implement strict access controls. For cloud-based agents, data is processed according to our privacy policy. Many agents also offer local processing options where your data never leaves your device. Check each agent's privacy details before use.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4">
                <AccordionTrigger>How do I connect my own AI API keys?</AccordionTrigger>
                <AccordionContent>
                  You can manage your API connections in the "AI Connections" section of your settings. There, you can securely add API keys for various LLM providers. These connections allow you to use your own API quotas and ensure your data interactions remain under your control. We never store your actual API keys; they're encrypted and used only for making requests on your behalf.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FAQ;
